import {DateTime} from 'luxon';
import {FC, MouseEventHandler, useCallback, useEffect, useMemo, useState} from 'react';
import {TimePickerWheelConfig} from '~/common/components/TimePickerWheel/TimePickerWheel';
import {parseTime} from '~/common/utils';
import {Button} from '~library/atoms/Button';
import {Input} from '~library/atoms/Input';
import {ResponsiveModal} from '~library/organisms/ResponsiveModal';

interface CustomTimeProps {
  time: string;
  onChange: (time: string) => void;
  onClose: () => void;
  open?: boolean;
  qaIdPrefix?: string;
}

export const CustomTime: FC<CustomTimeProps> = ({
  time,
  onChange,
  onClose,
  open = false,
  qaIdPrefix = 'event',
}) => {
  const [customTime, setCustomTime] = useState(time);
  const [customTimeError, setCustomTimeError] = useState<boolean>(false);

  const defaultMeridiem = useMemo(() => {
    const match: RegExpMatchArray | null = parseTime(time);
    if (!match?.groups) return '';
    return match.groups.meridiem || '';
  }, [time]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    setCustomTime(time);
  }, [time, open]);

  const changeTime = useCallback(
    (newValue: InputOption | string) => {
      const newTime = typeof newValue === 'string' ? newValue : newValue.value;
      const match: RegExpMatchArray | null = parseTime(newTime);
      setCustomTime(
        match?.groups?.meridiem ? newTime.trim() : `${newTime} ${defaultMeridiem}`.trim()
      );
      setCustomTimeError(false);
    },
    [defaultMeridiem]
  );

  const saveTime: MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      const savedTimePieces = parseTime(time);
      const {meridiem: savedMeridiem = 'AM'}: TimePickerWheelConfig = savedTimePieces?.groups || {};
      const newTimePieces = parseTime(customTime, false);
      if (!newTimePieces) {
        setCustomTimeError(true);
        return;
      }
      const {minute}: TimePickerWheelConfig = newTimePieces.groups;
      let {hour, meridiem}: TimePickerWheelConfig = newTimePieces.groups;
      if (!hour || !minute) {
        setCustomTimeError(true);
        return;
      }
      if (!meridiem) meridiem = savedMeridiem; // Use saved meridiem if not provided or malformed
      if (parseInt(hour, 10) < 1 || parseInt(hour, 10) > 12) {
        setCustomTimeError(true);
        return;
      }
      if (hour === '12' && meridiem.toUpperCase() === 'AM') hour = '00'; // Handle 12 AM case
      if (
        parseInt(minute, 10) < 0 ||
        parseInt(minute, 10) > 59 ||
        !['AM', 'PM'].includes(meridiem.toUpperCase())
      ) {
        setCustomTimeError(true);
        return;
      }

      const datetime = DateTime.now().set({
        hour: parseInt(hour, 10) + (meridiem.toUpperCase() === 'PM' ? 12 : 0),
        minute: parseInt(minute, 10),
      });
      if (!datetime.isValid) {
        setCustomTimeError(true);
        return;
      }
      setCustomTimeError(false);
      onChange(`${hour}:${minute} ${meridiem.toUpperCase()}`);
      onClose();
    },
    [customTime, onChange, onClose, time]
  );

  return (
    <ResponsiveModal
      isOpen={open}
      onClose={onClose}
      title="Custom time"
      primaryAction={saveTime}
      primaryActionDisabled={!customTime || customTimeError}
      primaryText="Save"
      closeQaId="close-custom-time-modal"
      titleQaId="custom-time-modal-label"
      modalId={`${qaIdPrefix}-custom-time`}
    >
      <div className="start-end-controller__custom-time">
        <Input
          type="time"
          value={customTime}
          onChange={changeTime}
          data-qa-id="custom-time-input"
          placeholder="00:00 AM"
          isClearable={false}
          isSearchable
          required
          error={customTimeError ? 'Invalid time.' : undefined}
          showSelectedCheck
        />
        <div className="start-end-controller__custom-time__ctas">
          <Button
            variant="secondary"
            onClick={onClose}
            data-qa-id={`${qaIdPrefix}-cancel-custom-time`}
            fullWidth
            size="large"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={saveTime}
            data-qa-id={`${qaIdPrefix}-save-custom-time`}
            fullWidth
            size="large"
            disabled={!customTime || customTimeError}
          >
            Save
          </Button>
        </div>
      </div>
    </ResponsiveModal>
  );
};
